import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useReadContract } from 'wagmi';
import { useAuth } from '../context/AuthContext';
import { SessionPage } from '../components/SessionPage';
import { COHORT_ABI } from '../contract/abi';
import { addToCohortHistory, getCohortById } from '../utils/cohortHistory';

// Validate cohort ID format (0x + 64 hex chars)
function isValidCohortId(id: string): id is `0x${string}` {
  return /^0x[a-fA-F0-9]{64}$/.test(id);
}

export function CohortPage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const { passkeyAddress } = useAuth();
  const [cohortTitle, setCohortTitle] = useState<string | null>(null);

  const contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS as `0x${string}`;

  // Validate cohort ID format
  const validCohortId = cohortId && isValidCohortId(cohortId) ? cohortId : null;

  // Try to get title from localStorage first (set by MainPage when navigating)
  useEffect(() => {
    if (validCohortId) {
      const cached = getCohortById(validCohortId);
      if (cached?.title) {
        setCohortTitle(cached.title);
      }
    }
  }, [validCohortId]);

  // Pre-fetch signers to warm the cache (SessionPage will use this)
  const { isLoading: isLoadingSigners } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getSigners',
    args: validCohortId ? [validCohortId] : undefined,
    query: { enabled: !!validCohortId },
  });

  // Look up cohort title from on-chain registry (for existing cohorts)
  const { data: onChainTitle, isLoading: isLoadingTitle } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'names',
    args: validCohortId ? [validCohortId] : undefined,
    query: { enabled: !!validCohortId },
  });

  const isLoading = isLoadingSigners || isLoadingTitle;

  // Update title from on-chain if available (takes precedence for existing cohorts)
  useEffect(() => {
    if (onChainTitle && typeof onChainTitle === 'string' && onChainTitle.length > 0) {
      setCohortTitle(onChainTitle);
    }
  }, [onChainTitle]);

  // Add to history when we have both cohort ID and title
  useEffect(() => {
    if (validCohortId && cohortTitle) {
      addToCohortHistory(cohortTitle, validCohortId);
    }
  }, [validCohortId, cohortTitle]);

  // Redirect to home if invalid cohort ID format
  useEffect(() => {
    if (cohortId && !validCohortId) {
      navigate('/', { replace: true });
    }
  }, [cohortId, validCohortId, navigate]);


  // Show loading state
  if (isLoading || !validCohortId) {
    return (
      <div className="container">
        <div className="card">
          <p className="text-muted">Loading cohort...</p>
        </div>
      </div>
    );
  }

  // If no title available (direct URL access to unknown cohort), show error
  if (!cohortTitle) {
    return (
      <div className="container">
        <div className="card">
          <h1>Unknown Cohort</h1>
          <p className="text-muted">
            This cohort doesn't exist on-chain yet, and no title was found in your history.
          </p>
          <p className="text-muted">
            To create a new cohort, go to the home page and enter a title.
          </p>
          <button onClick={() => navigate('/')} className="btn-primary" style={{ marginTop: 'var(--space-md)' }}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <SessionPage
      cohortId={validCohortId}
      cohortTitle={cohortTitle}
      passkeyAddress={passkeyAddress}
    />
  );
}
